import { Check, ChevronDown, X } from 'lucide-react';
import * as React from 'react';

import { Popover, PopoverContent, PopoverTrigger } from '../core/popover';
import { cn } from '../../utils';

export interface MultiSelectFilterOption {
  value: string;
  label: string;
}

export interface MultiSelectFilterProps {
  options: MultiSelectFilterOption[];
  value: string[];
  onChange: (next: string[]) => void;
  label: string;
  clearLabel?: string;
  emptyLabel?: string;
  triggerClassName?: string;
  badgeClassName?: string;
  contentClassName?: string;
  itemActiveClassName?: string;
  align?: 'start' | 'center' | 'end';
  disabled?: boolean;
}

export function MultiSelectFilter({
  options,
  value,
  onChange,
  label,
  clearLabel,
  emptyLabel,
  triggerClassName,
  badgeClassName,
  contentClassName,
  itemActiveClassName,
  align = 'start',
  disabled,
}: MultiSelectFilterProps) {
  const [open, setOpen] = React.useState(false);

  const toggle = (val: string) => {
    if (value.includes(val)) {
      onChange(value.filter((v) => v !== val));
    } else {
      onChange([...value, val]);
    }
  };

  const clear = () => onChange([]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          className={cn(
            'inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm outline-none transition-colors hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-gray-300 disabled:cursor-not-allowed disabled:opacity-50',
            triggerClassName,
          )}
        >
          <span>{label}</span>
          {value.length > 0 && (
            <span
              className={cn(
                'inline-flex min-w-[1.5rem] items-center justify-center rounded-full bg-gray-900 px-1.5 py-0.5 text-xs tabular-nums text-white',
                badgeClassName,
              )}
            >
              {value.length}
            </span>
          )}
          <ChevronDown
            className={cn('h-4 w-4 transition-transform', open && 'rotate-180')}
            aria-hidden="true"
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align={align}
        className={cn('w-64 p-1', contentClassName)}
      >
        {options.length === 0 ? (
          <p className="px-3 py-4 text-center text-sm text-gray-500">
            {emptyLabel ?? 'No options'}
          </p>
        ) : (
          <ul role="listbox" aria-multiselectable className="max-h-72 overflow-auto">
            {options.map((option) => {
              const isActive = value.includes(option.value);
              return (
                <li key={option.value} role="option" aria-selected={isActive}>
                  <button
                    type="button"
                    onClick={() => toggle(option.value)}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm text-gray-700 outline-none transition-colors hover:bg-gray-100 focus-visible:bg-gray-100',
                      isActive && (itemActiveClassName ?? 'bg-gray-100 text-gray-900'),
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border border-gray-300',
                        isActive && 'border-current bg-current',
                      )}
                      aria-hidden="true"
                    >
                      <Check
                        className={cn(
                          'h-3 w-3 text-white',
                          isActive ? 'opacity-100' : 'opacity-0',
                        )}
                        strokeWidth={3}
                      />
                    </span>
                    <span className="flex-1 truncate">{option.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        {value.length > 0 && (
          <div className="mt-1 border-t border-gray-100 pt-1">
            <button
              type="button"
              onClick={clear}
              className="flex w-full items-center justify-center gap-1.5 rounded-md px-2 py-2 text-xs font-medium text-gray-500 outline-none transition-colors hover:bg-gray-50 hover:text-gray-700 focus-visible:bg-gray-50"
            >
              <X className="h-3 w-3" aria-hidden="true" />
              {clearLabel ?? 'Clear all'}
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
